import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useTentStatus } from '../custom-hooks/useTentStatus';
import { useTranslation } from 'react-i18next';

const TentInfo = ({ tent, participants }) => {
    const { status, reason } = useTentStatus(tent);

    const { t } = useTranslation();

    if (!tent) {
        return <p>Loading tent information...</p>;
    }

    return (
        <div className="lg:w-1/3 w-full bg-white rounded-2xl shadow p-6 flex flex-col">
            <h2 className="text-tertiary font-medium text-lg mb-3">
                {t('tent-management-page.Tent Information')}
            </h2>
            <div className="text-sm text-gray-600 mb-4 space-y-1">
                <p>
                    <strong>{t('tent-management-page.Name')}:</strong>
                    {tent.name || 'N/A'}
                </p>
                <p>
                    <strong>{t('tent-management-page.Capacity')}:</strong>
                    {tent.capacity || 'N/A'}
                </p>
                <p>
                    <strong>
                        {t('tent-management-page.Number of participants')}:
                    </strong>
                    {participants.length || 0}
                </p>
                <p>
                    <strong>
                        {t('tent-management-page.Number of Staffs')}:
                    </strong>
                    {tent?.staffs?.length || 0}
                </p>
                <p>
                    <strong>Status:</strong>
                    {status === 'PROBLEMATIC' && (
                        <span className="text-red-600 font-semibold">
                            Problematic
                        </span>
                    )}
                    {status === 'OKAY' && (
                        <span className="text-green-600 font-semibold">
                            Okay
                        </span>
                    )}
                    {!status && (
                        <span className="text-gray-500 font-medium">
                            Loading...
                        </span>
                    )}
                </p>

                {status === 'PROBLEMATIC' && reason && (
                    <p className="text-sm text-red-500">
                        {t('tent-management-page.Reason')}: {reason}
                    </p>
                )}
            </div>

            <Droppable droppableId={`tent-${tent.id}`}>
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex-1 overflow-auto space-y-3 bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 min-h-[300px]"
                    >
                        {participants.map((p, index) => (
                            <Draggable
                                key={`tent-${tent.id}-participant-${p.id}`}
                                draggableId={String(p.id)}
                                index={index}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="flex items-center gap-4 p-3 bg-white rounded shadow hover:bg-gray-100 transition"
                                    >
                                        <img
                                            src={`https://randomuser.me/api/portraits/${
                                                p.gender === 'Female'
                                                    ? 'women'
                                                    : 'men'
                                            }/1.jpg`}
                                            alt={p.first_name}
                                            className="w-12 h-12 rounded-full object-cover border"
                                        />
                                        <div>
                                            <p className="font-medium text-gray-800">
                                                {p.first_name} {p.last_name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {p.gender}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </div>
    );
};

export default TentInfo;
